import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Search, LayoutGrid } from "lucide-react";
import * as Icons from "lucide-react";
import { LAYERS, LAYER_COLORS, ELEMENT_TYPES } from "@/constants/archimate";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function ModelTree({
  elements,
  onSelectElement,
  onAddElement,
  onAddToView,
  selectedElementId,
  checkedIds,
  onToggleChecked,
  onClearChecked,
  onCreateViewFromChecked,
  readOnly = false,
}) {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [createViewOpen, setCreateViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");

  const filtered = elements.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));

  const submitCreateView = () => {
    if (!newViewName.trim()) return;
    onCreateViewFromChecked(newViewName.trim());
    setNewViewName("");
    setCreateViewOpen(false);
  };

  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100" data-testid="model-tree-sidebar">
      <div className="p-3 border-b border-slate-800">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
          <Input
            data-testid="model-tree-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search elements..."
            className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {LAYERS.map((layer) => {
          const layerElements = filtered.filter((e) => e.layer === layer.key);
          const colors = LAYER_COLORS[layer.key];
          const isCollapsed = collapsed[layer.key];
          return (
            <div key={layer.key} className="mb-1">
              <div className="flex items-center justify-between px-3 py-1.5 group">
                <button
                  data-testid={`layer-toggle-${layer.key}`}
                  onClick={() => setCollapsed((c) => ({ ...c, [layer.key]: !c[layer.key] }))}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200"
                >
                  {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  <span className="h-2 w-2 rounded-full" style={{ background: colors.border }} />
                  {layer.label}
                  <span className="text-slate-600">({layerElements.length})</span>
                </button>
                <button
                  data-testid={`add-element-${layer.key}-button`}
                  onClick={() => onAddElement(layer.key)}
                  className={`text-slate-400 hover:text-white transition-opacity ${readOnly ? "hidden" : "opacity-0 group-hover:opacity-100"}`}
                >
                  <Plus size={14} />
                </button>
              </div>
              {!isCollapsed && (
                <div className="pl-6">
                  {layerElements.map((el) => {
                    const typeInfo = ELEMENT_TYPES[el.type] || {};
                    const Icon = Icons[typeInfo.icon] || Icons.Box;
                    return (
                      <div
                        key={el.id}
                        data-testid={`model-tree-element-${el.id}`}
                        onClick={() => onSelectElement(el.id)}
                        onDoubleClick={readOnly ? undefined : () => onAddToView(el.id)}
                        title={readOnly ? "Tap to view details" : "Click to select · Double-click to add to current view"}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs truncate ${
                          selectedElementId === el.id ? "bg-slate-700 text-white" : "text-slate-300 hover:bg-slate-800"
                        }`}
                      >
                        {!readOnly && (
                          <Checkbox
                            data-testid={`model-tree-checkbox-${el.id}`}
                            checked={checkedIds.includes(el.id)}
                            onCheckedChange={() => onToggleChecked(el.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="border-slate-600 shrink-0"
                          />
                        )}
                        <Icon size={12} style={{ color: colors.border }} />
                        <span className="truncate">{el.name}</span>
                      </div>
                    );
                  })}
                  {layerElements.length === 0 && <div className="px-2 py-1 text-[11px] text-slate-600">No elements</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!readOnly && checkedIds.length > 0 && (
        <div
          data-testid="tree-selection-action-bar"
          className="border-t border-slate-800 p-2.5 flex items-center justify-between gap-2 bg-slate-800/70"
        >
          <span className="text-xs text-slate-300" data-testid="tree-selection-count">
            {checkedIds.length} selected
          </span>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="clear-tree-selection-button"
              onClick={onClearChecked}
              className="text-xs text-slate-400 hover:text-white px-1.5 py-1"
            >
              Clear
            </button>
            <Button
              size="sm"
              data-testid="create-view-from-selection-button"
              onClick={() => setCreateViewOpen(true)}
              className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
            >
              <LayoutGrid size={12} className="mr-1" />
              Create View
            </Button>
          </div>
        </div>
      )}
      <Dialog open={createViewOpen} onOpenChange={setCreateViewOpen}>
        <DialogContent data-testid="create-view-from-selection-dialog">
          <DialogHeader>
            <DialogTitle>New View from Selection</DialogTitle>
            <DialogDescription>
              Creates a new view with the {checkedIds.length} selected element(s), auto-arranged with their relationships drawn in.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="create-view-from-selection-name-input"
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            placeholder="View name"
            onKeyDown={(e) => e.key === "Enter" && submitCreateView()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateViewOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitCreateView} data-testid="create-view-from-selection-submit-button">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
